import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import AxiosUrl from "../AxiosUrl";
import { toast } from "react-toastify";
import "./TicketModal.css";

function TicketModal({ open, onClose, onSuccess, report, uid }) {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    ticketType: "general",
    priority: "medium",
    status: "pending",
    notes: "",
    assignedTo: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (report) {
      setFormData({
        title: report.title || "",
        description: report.description || "",
        ticketType: report.ticketType || "general",
        priority: report.priority || "medium",
        status: report.status || "pending",
        notes: report.notes || "",
        assignedTo: report.assignedTo || "",
      });
    } else {
      setFormData({
        title: "",
        description: "",
        ticketType: "general",
        priority: "medium",
        status: "pending",
        notes: "",
        assignedTo: "",
      });
    }
  }, [report]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!formData.description.trim()) {
      toast.error("Description is required");
      return;
    }

    setLoading(true);

    try {
      let response;
      if (report) {
        // Update existing report
        response = await AxiosUrl.post(`/update-report/${uid}`, {
          reportId: report._id,
          data: formData,
        });
      } else {
        // Create new report
        response = await AxiosUrl.post(`/create-report/${uid}`, {
          data: formData,
        });
      }

      if (response.data.success) {
        toast.success(response.data.message);
        onSuccess();
      } else {
        toast.error(response.data.error || "Operation failed");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to save report");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="report-modal">
      <DialogTitle className="report-modal-title">
        {report ? "Edit Support Ticket" : "Create New Support Ticket"}
      </DialogTitle>
      <DialogContent className="report-modal-content">
        <div className="report-form">
          <TextField
            label="Title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            fullWidth
            required
            variant="outlined"
            margin="normal"
            placeholder="Enter report title"
          />

          <TextField
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            fullWidth
            required
            multiline
            rows={4}
            variant="outlined"
            margin="normal"
            placeholder="Enter detailed description"
          />

          <div className="report-form-row">
            <TextField
              select
              label="Ticket Type"
              name="ticketType"
              value={formData.ticketType}
              onChange={handleChange}
              variant="outlined"
              margin="normal"
              className="report-form-field"
            >
              <MenuItem value="user">User Issue</MenuItem>
              <MenuItem value="payment">Payment Issue</MenuItem>
              <MenuItem value="subscription">Subscription Issue</MenuItem>
              <MenuItem value="general">General Query</MenuItem>
              <MenuItem value="bug">Bug Report</MenuItem>
              <MenuItem value="feature">Feature Request</MenuItem>
            </TextField>

            <TextField
              select
              label="Priority"
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              variant="outlined"
              margin="normal"
              className="report-form-field"
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
            </TextField>
          </div>

          {report && (
            <div className="report-form-row">
              <TextField
                select
                label="Status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                variant="outlined"
                margin="normal"
                className="report-form-field"
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="in-progress">In Progress</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </TextField>
            </div>
          )}

          <TextField
            label="Assigned To"
            name="assignedTo"
            value={formData.assignedTo}
            onChange={handleChange}
            fullWidth
            variant="outlined"
            margin="normal"
            placeholder="Admin username or ID"
          />

          <TextField
            label="Notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            margin="normal"
            placeholder="Additional notes (optional)"
          />
        </div>
      </DialogContent>
      <DialogActions className="report-modal-actions">
        <Button onClick={onClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
          className="report-submit-button"
        >
          {loading ? "Saving..." : report ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TicketModal;
